import { memo, useEffect, useMemo, useState } from 'react';
import BackgroundImageEmptyState from './BackgroundImageEmptyState';
import BrandKitMediaCard from '@/Pages/BrandKit/components/MediaCard/BrandKitMediaCard';
import MediaPickerModal from '@/Pages/BrandKit/components/MediaCard/MediaPickerModal';
import MediaPickerTabBar from '@/Pages/BrandKit/components/MediaCard/MediaPickerTabBar';
import BrandKitMediaSection from '@/Pages/BrandKit/components/MediaCard/BrandKitMediaSection';
import { useMediaPickerState } from '@/Pages/BrandKit/components/MediaCard/useMediaPickerState';
import { getMediaPickerConfirmDisabled } from '@/Pages/BrandKit/components/utils';
import FilesInput from '@/components/atoms/inputs/FilesInput/FilesInput';
import { BackgroundImageModalProps, PlatformBrandKitImage } from '@/Pages/BrandKit/types';
import { BRAND_KIT_ACCEPT_FORMATS, MEDIA_PICKER_TABS } from '@/Pages/BrandKit/constants';

function BackgroundImageModal({
  isOpen,
  onClose,
  uploadedImagesUrls,
  selectedImage: initialSelectedImage,
  onImagesUploaded,
  onImageSelected,
  onDelete,
  isUploading = false
}: BackgroundImageModalProps): JSX.Element {
  const [selectedImage, setSelectedImage] = useState<string | undefined>(initialSelectedImage);

  useEffect(() => {
    setSelectedImage(initialSelectedImage);
  }, [initialSelectedImage]);

  const { activeTab, setActiveTab, activeBrandKitId, setActiveBrandKitId, brandKits, activeKit, showUploadsOnly } =
    useMediaPickerState();

  const brandKitImages = useMemo(() => (activeKit?.design_elements as PlatformBrandKitImage[]) ?? [], [activeKit]);

  const confirmDisabled = useMemo(
    () =>
      getMediaPickerConfirmDisabled({
        selectedUrl: selectedImage,
        activeTab,
        brandKitMedia: brandKitImages,
        uploadsMediaUrls: uploadedImagesUrls
      }),
    [selectedImage, activeTab, brandKitImages, uploadedImagesUrls]
  );

  function handleBrandKitChange(brandKitId: string) {
    setActiveBrandKitId(brandKitId);
    setSelectedImage(undefined);
  }

  function handleConfirm() {
    if (selectedImage) {
      onImageSelected(selectedImage);
    }
    onClose();
  }

  return (
    <MediaPickerModal
      isOpen={isOpen}
      onClose={onClose}
      title="Background Image"
      onConfirm={handleConfirm}
      confirmDisabled={confirmDisabled}
      isConfirmLoading={isUploading}
    >
      {!showUploadsOnly && (
        <MediaPickerTabBar
          tabs={MEDIA_PICKER_TABS}
          activeTabId={activeTab}
          onTabChange={id => setActiveTab(id as 'uploads' | 'brandkit')}
        />
      )}

      {activeTab === 'uploads' && (
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-3">
            <p className="text-sm font-medium text-slate-700">Upload custom image</p>
            <FilesInput
              onFilesUpload={onImagesUploaded}
              accept={BRAND_KIT_ACCEPT_FORMATS.background}
              multiple={false}
              icon="IconCloudUpload"
              title="Upload Image"
              label="Click to browse or drag and drop an image."
              rootClassNames="h-[155px]"
            />
          </div>

          {uploadedImagesUrls.length > 0 && (
            <div className="flex flex-col gap-3">
              <p className="text-sm font-medium text-slate-700">Recently uploaded</p>
              <div data-testid="uploaded-images-grid" className="grid grid-cols-3 gap-4">
                {uploadedImagesUrls.map(url => (
                  <BrandKitMediaCard
                    key={url}
                    url={url}
                    isSelected={selectedImage === url}
                    onClick={() => setSelectedImage(url)}
                    type="image"
                    onDelete={() => onDelete(url)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'brandkit' && (
        <BrandKitMediaSection
          brandKits={brandKits}
          activeBrandKitId={activeBrandKitId}
          onBrandKitChange={handleBrandKitChange}
          images={brandKitImages}
          selectedUrl={selectedImage}
          onSelect={item => setSelectedImage(item.url)}
          imageEmptyState={<BackgroundImageEmptyState brandKitId={activeBrandKitId} />}
        />
      )}
    </MediaPickerModal>
  );
}

export default memo(BackgroundImageModal);
